import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { ConfigService } from '../core/config/config.service';
import {
  AlterarStatusRequest,
  ClienteFiltros,
  ClienteRequest,
  ClienteStatusHistoricoDTO,
  ClienteResponse,
  ClienteResumo,
  MotivoRequest,
  StatusCliente,
} from '../models/cliente.model';
import { Pagina } from '../models/pagina.model';
import {
  AtualizarVersaoClienteRequestDTO,
  ClienteVersaoHistoricoDTO,
} from '../models/versao.model';
import { ClienteServidorResponseDTO, VincularServidorRequestDTO } from '../models/servidor.model';

@Injectable({ providedIn: 'root' })
export class ClienteService {
  private readonly http = inject(HttpClient);
  private readonly config = inject(ConfigService);

  /** `pagina` base 0. Filtros vazios não vão na query. */
  listar(
    filtros: ClienteFiltros,
    pagina: number,
    tamanho: number,
  ): Observable<Pagina<ClienteResumo>> {
    let params = new HttpParams().set('pagina', pagina).set('tamanho', tamanho);
    const busca = filtros.busca?.trim();
    if (busca) {
      params = params.set('busca', busca);
    }
    if (filtros.status) {
      params = params.set('status', filtros.status);
    }
    if (filtros.uf) {
      params = params.set('uf', filtros.uf);
    }
    if (filtros.servidorId) {
      params = params.set('servidorId', filtros.servidorId);
    }
    if (filtros.situacaoVersao) {
      params = params.set('situacaoVersao', filtros.situacaoVersao);
    }
    return this.http.get<Pagina<ClienteResumo>>(`${this.apiUrl}/clientes`, { params });
  }

  buscar(id: number): Observable<ClienteResponse> {
    return this.http.get<ClienteResponse>(`${this.apiUrl}/clientes/${id}`);
  }

  criar(request: ClienteRequest): Observable<ClienteResponse> {
    return this.http.post<ClienteResponse>(`${this.apiUrl}/clientes`, request);
  }

  atualizar(id: number, request: ClienteRequest): Observable<ClienteResponse> {
    return this.http.put<ClienteResponse>(`${this.apiUrl}/clientes/${id}`, request);
  }

  alterarStatus(
    id: number,
    novoStatus: StatusCliente,
    motivo: string,
  ): Observable<ClienteResponse> {
    const corpo: AlterarStatusRequest = { novoStatus, motivo };
    return this.http.post<ClienteResponse>(`${this.apiUrl}/clientes/${id}/status`, corpo);
  }

  liberar(id: number, motivo: string): Observable<ClienteResponse> {
    const corpo: MotivoRequest = { motivo };
    return this.http.post<ClienteResponse>(`${this.apiUrl}/clientes/${id}/liberar`, corpo);
  }

  bloquear(id: number, motivo: string): Observable<ClienteResponse> {
    const corpo: MotivoRequest = { motivo };
    return this.http.post<ClienteResponse>(`${this.apiUrl}/clientes/${id}/bloquear`, corpo);
  }

  /** `pagina` base 0. */
  historicoStatus(
    id: number,
    pagina: number,
    tamanho = 10,
  ): Observable<Pagina<ClienteStatusHistoricoDTO>> {
    const params = new HttpParams().set('pagina', pagina).set('tamanho', tamanho);
    return this.http.get<Pagina<ClienteStatusHistoricoDTO>>(
      `${this.apiUrl}/clientes/${id}/historico-status`,
      { params },
    );
  }

  servidorDoCliente(id: number): Observable<ClienteServidorResponseDTO> {
    return this.http.get<ClienteServidorResponseDTO>(`${this.apiUrl}/clientes/${id}/servidor`);
  }

  vincularServidor(
    id: number,
    servidorId: number,
    observacao: string | null,
  ): Observable<ClienteServidorResponseDTO> {
    const corpo: VincularServidorRequestDTO = { servidorId, observacao };
    return this.http.put<ClienteServidorResponseDTO>(
      `${this.apiUrl}/clientes/${id}/servidor`,
      corpo,
    );
  }

  desvincularServidor(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/clientes/${id}/servidor`);
  }

  atualizarVersao(
    id: number,
    versaoId: number,
    observacao: string | null,
  ): Observable<ClienteResponse> {
    const corpo: AtualizarVersaoClienteRequestDTO = { versaoId, observacao };
    return this.http.put<ClienteResponse>(`${this.apiUrl}/clientes/${id}/versao`, corpo);
  }

  /** `pagina` base 0. */
  historicoVersoes(
    id: number,
    pagina: number,
    tamanho = 10,
  ): Observable<Pagina<ClienteVersaoHistoricoDTO>> {
    const params = new HttpParams().set('pagina', pagina).set('tamanho', tamanho);
    return this.http.get<Pagina<ClienteVersaoHistoricoDTO>>(
      `${this.apiUrl}/clientes/${id}/versoes`,
      { params },
    );
  }

  private get apiUrl(): string | undefined {
    return this.config.settings()?.apiUrl;
  }
}
